// Mapper to create WebApiMemberDtoAddMemberObjectDto from uploadFields and memberInFile
// Assumes uploadFields is an array of objects with a 'type' property matching MemberFieldTypes
// and memberInFile is an array of CSV values in the same order as uploadFields

import { resolveCountryCode } from "../Helpers/CountryOptions";
import { formatDateString, getDateStringFromDate, excelSerialToDate, isExcelSerialDate } from "../Helpers/DateHelpers";
import { WebApiMemberDtoAddMemberObjectDto, WebApiMemberDtoCustomMemberFieldValueDto, WebApiMemberDtoGroupDto, WebApiMemberFieldsDtoMemberUploadFieldDto } from "../Repository/eventbookyapi";
import { CustomField } from "../Types/CustomField";
import { UI_CONTROLS } from "../Types/FieldTypes";
import { GroupType } from "../Types/GroupType";
import { MemberFieldTypes } from "../Types/MemberFieldTypes";
import { UiControlType } from "../Types/UiControlType";
import { mapToMemberStatus } from "./mapGroupToListItem";

// import { WebApiMemberDtoAddMemberObjectDto } from "../../Repository/eventbookyapi";
// Uncomment and adjust the import above to match your DTO location

// Helper: map field type to DTO property name
const memberFieldTypeToDtoKey: Record<MemberFieldTypes, string> = {
    [MemberFieldTypes.UniqueId]: "uniqueId",
    [MemberFieldTypes.PrimaryMemberNumber]: "primaryMemberNumber",
    [MemberFieldTypes.FirstName]: "firstName",
    [MemberFieldTypes.LastName]: "lastName",
    [MemberFieldTypes.Email]: "email",
    [MemberFieldTypes.Email2]: "email2",
    [MemberFieldTypes.PhoneNumber]: "phoneNumber",
    [MemberFieldTypes.Address]: "address",
    [MemberFieldTypes.PostalCode]: "postalCode",
    [MemberFieldTypes.PostalArea]: "postalArea",
    [MemberFieldTypes.Title]: "title",
    [MemberFieldTypes.Country]: "country",
    [MemberFieldTypes.Company]: "company",
    [MemberFieldTypes.Extra1]: "extra1",
    [MemberFieldTypes.Extra2]: "extra2",
    [MemberFieldTypes.Extra3]: "extra3",
    [MemberFieldTypes.Group]: "groups",
    [MemberFieldTypes.Status]: "status",
    [MemberFieldTypes.Payment]: "paymentAmountCurrentYear", // Adjust as needed
    [MemberFieldTypes.EmailNotificationPreference]: "emailNotificationPreference",
    [MemberFieldTypes.SortOrder]: "sortOrder",
    [MemberFieldTypes.PersonalNumber]: "personalNumber",
    [MemberFieldTypes.PhoneNumber2]: "phoneNumber2",
    [MemberFieldTypes.AdressRow2]: "adressRow2",
    [MemberFieldTypes.AdressRow3]: "adressRow3",
    [MemberFieldTypes.JoinedDate]: "joinedDate",
    [MemberFieldTypes.WebPage]: "webPage",
    [MemberFieldTypes.Membership]: "membership",
    [MemberFieldTypes.MembershipAddon]: "membership", // obsolete field type; add-ons are imported via the Membership column
    [MemberFieldTypes.Custom]: "custom", // Custom fields may need special handling
    [MemberFieldTypes.LastModified]: "unspecified", // view-only column, never part of an upload template
    [MemberFieldTypes.Created]: "unspecified", // view-only column, never part of an upload template
    [MemberFieldTypes.UnSpecified]: "unspecified"
};

// Main mapper function
export function mapUploadFieldsToBulkMemberDto(uploadFields: WebApiMemberFieldsDtoMemberUploadFieldDto[], memberInFile: string[], headers: string[], groups: WebApiMemberDtoGroupDto[]): WebApiMemberDtoAddMemberObjectDto {
    const dto: WebApiMemberDtoAddMemberObjectDto = {};
    const customFields: WebApiMemberDtoCustomMemberFieldValueDto[] = [];

    // Helper to map yes/no/ja/nej/1/0 to 'true'/'false' string, returns undefined if not matched
    function mapYesNoToBooleanString(value: string): string | undefined {
        const v = value.trim().toLocaleLowerCase();
        if (v === "yes" || v === "1" || v === "ja") return "true";
        if (v === "no" || v === "0" || v === "nej") return "false";
        return undefined;
    }


    // Assign all other fields except payment fields
    uploadFields.forEach((field, idx) => {
        if (field.fieldType == MemberFieldTypes.Custom) {
            headers.forEach((header, headerIdx) => {
                if (header === field.customFieldName) {
                    const value = trimFromCsv(memberInFile[headerIdx]);

                    if (value) {
                        if (field.uiControl == UiControlType.YesNo) {
                            const boolString = mapYesNoToBooleanString(value);
                            if (boolString !== undefined) {
                                customFields.push({ fieldId: field.id!, fieldValue: boolString });
                            }
                        } else if (field.uiControl == UiControlType.Date) {
                            // Handle Excel serial dates
                            if (isExcelSerialDate(value)) {
                                const date = excelSerialToDate(value);
                                customFields.push({ fieldId: field.id!, fieldValue: date?.toISOString() || value });
                            } else {
                                customFields.push({ fieldId: field.id!, fieldValue: value });
                            }
                        } else {
                            customFields.push({ fieldId: field.id!, fieldValue: value });
                        }
                    }

                }
            });
        }
    });
    dto.customFieldValues = customFields;

    // Assign all fields using uploadFields index (which matches template column order)
    uploadFields.forEach((field, idx) => {
        const type = field.fieldType;
        const key = memberFieldTypeToDtoKey[type!];

        // Handle payment fields by year using customFieldName and correct column index
        if (type === MemberFieldTypes.Payment) {
            const value = trimFromCsv(memberInFile[idx]);
            const numericValue = value === "" ? null : Number(value);
            const yearStr = field.customFieldName ?? "";
            const currentYear = new Date().getFullYear();
            if (yearStr.indexOf(String(currentYear - 2)) !== -1) {
                dto.paymentAmountYearMinusTwo = numericValue;
            } else if (yearStr.indexOf(String(currentYear - 1)) !== -1) {
                dto.paymentAmountYearMinusOne = numericValue;
            } else if (yearStr.indexOf(String(currentYear)) !== -1) {
                dto.paymentAmountCurrentYear = numericValue;
            } else if (yearStr.indexOf(String(currentYear + 1)) !== -1) {
                dto.paymentAmountNextYear = numericValue;
            }
            return;
        }

        let value = trimFromCsv(memberInFile[idx]);
        if (key === "groups") {
            // Use the actual value from the Excel file, or fall back to default groups if empty
            dto.groups = value ? validateAndFilterGroups(value, groups) : getDefaultGroups(groups);
        } else if (key === "status") {
            dto.status = mapToMemberStatus(value);
        } else if (key === "joinedDate") {
            // Handle Excel serial dates - only set if value is not empty
            const trimmedValue = value?.trim();
            if (trimmedValue) {
                if (isExcelSerialDate(trimmedValue)) {
                    dto.joinedDate = excelSerialToDate(trimmedValue) ?? undefined;
                } else {
                    const dateObj = new Date(trimmedValue);
                    dto.joinedDate = !isNaN(dateObj.getTime()) ? dateObj : undefined;
                }
            }
        } else if (key === "country") {
            (dto as any)[key] = resolveCountryCode(value);
        } else if (key === "emailNotificationPreference") {
            dto.emailNotificationPreference = mapYesNoToBooleanString(value) ? 1 : 0;
        } else {
            (dto as any)[key] = value;
        }
    });
    return dto;
}

const trimFromCsv = (value: string) => {
    return value ? value.trim() : ""
}

const getDefaultGroups = (groups: WebApiMemberDtoGroupDto[]) => {
    var result = groups.filter((p: WebApiMemberDtoGroupDto) => p.groupType == GroupType.Default);
    return result.map(p => p.name).join(",")
}

const validateAndFilterGroups = (value: string, groups: WebApiMemberDtoGroupDto[]) => {
    // Split by comma, trim whitespace, and validate each group name
    const groupNames = value.split(",").map(g => g.trim()).filter(g => g.length > 0);
    const validGroupNames = groups.map(g => g.name?.toLowerCase());

    // Filter to only include groups that exist in the system
    const validGroups = groupNames.filter(name =>
        validGroupNames.includes(name.toLowerCase())
    );

    // If no valid groups found, fall back to default groups
    if (validGroups.length === 0) {
        return getDefaultGroups(groups);
    }

    return validGroups.join(",");
}
